import { Typography } from '@material-ui/core'
import { useMemo, useState } from 'react'
import { Control, Controller } from 'react-hook-form'
import { useDebounce } from 'usehooks-ts'

import { Autocomplete } from '@/components/auto-complete'
import { useGetHerbicides } from '@/hooks/queries/use-get-herbicides'

import { SaveHerbicideTreatmentForm } from '../../hooks/use-save-herbicide-treatment-form'
import { useStyles } from './styles'

interface HerbicideAutocompleteProps {
  control: Control<SaveHerbicideTreatmentForm>
  index: number
}

export function HerbicideAutocomplete({
  control,
  index,
}: HerbicideAutocompleteProps) {
  const classes = useStyles()

  const [search, setSearch] = useState('')
  const [isActive, setIsActive] = useState(false)
  const debouncedSearch = useDebounce(search, 1000)

  const { data, isFetching, isError } = useGetHerbicides({
    search: debouncedSearch,
  })

  const isLoading = useMemo(() => {
    const isTyping = search !== debouncedSearch

    return isActive && (isFetching || isTyping)
  }, [search, debouncedSearch, isFetching])

  return (
    <Controller
      control={control}
      name={`herbicides.${index}.herbicide`}
      defaultValue={null as any}
      render={({
        field: { onChange, value, onBlur, ref, name },
        fieldState: { error },
      }) => (
        <Autocomplete
          id={name}
          options={data || []}
          value={value}
          freeSolo
          inputTextProps={{
            ref: ref,
            label: 'Selecione o herbicida',
            error: (!isFetching && isError) || !!error,
            helperText:
              !isFetching && isError
                ? 'Não foi possível buscar os herbicidas.'
                : error?.message,
          }}
          noOptionsText='Nenhum herbicida encontrado.'
          loading={isLoading}
          onBlur={() => {
            onBlur()
            setIsActive(false)
          }}
          onFocus={() => {
            setIsActive(true)
          }}
          onChange={(_, newValue) => {
            onChange(newValue)
          }}
          getOptionSelected={(option, value) => option.id === value.id}
          getOptionLabel={(option) =>
            option?.herbicide_name ? option.herbicide_name : ''
          }
          filterOptions={(options, params) =>
            options.filter((option) => {
              if (!params.inputValue) return true

              const searchValue = params.inputValue.toLowerCase()

              return (
                option.herbicide_name.toLowerCase().includes(searchValue) ||
                option.supplier.toLowerCase().includes(searchValue) ||
                option.registration_number.toString().includes(searchValue)
              )
            })
          }
          renderOption={(option) => (
            <div className={classes.labelContainer}>
              <Typography>{option.herbicide_name}</Typography>
              <Typography>
                {option.supplier} | {option.registration_number}
              </Typography>
            </div>
          )}
          onInputChange={(_, newInputValue) => {
            setSearch(newInputValue)
          }}
        />
      )}
    />
  )
}
